import { useState } from "react";
import { Button, Card, Checkbox, Form, Input } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate, Navigate } from "react-router-dom";
import { http, getCookies } from "../../utils";
import { onLoginSuccess } from "../../auth";
import InstallAppButton from "../../components/InstallAppButton";
import { COLOR_PRIMARY, FONT, SAFE_AREA, TEXT, TOUCH_MIN } from "../../config/uiTokens";

const LOGIN_REMEMBER_ACCOUNT_KEY = "login_remember_account";

export default function Login() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const hasToken = getCookies("authToken");
  const [loginError, setLoginError] = useState("");
  const [loading, setLoading] = useState(false);
  const [savedAccount] = useState(() =>
    typeof localStorage !== "undefined"
      ? localStorage.getItem(LOGIN_REMEMBER_ACCOUNT_KEY) || ""
      : ""
  );

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const clearError = () => setLoginError("");

  const onFinish = async (values) => {
    setLoginError("");
    setLoading(true);
    try {
      const {
        data: { results },
      } = await http.post("/user/myfleet-web-login", {
        account: values.account,
        password: values.password,
      });
      if (values.remember) {
        localStorage.setItem(LOGIN_REMEMBER_ACCOUNT_KEY, values.account);
      } else {
        localStorage.removeItem(LOGIN_REMEMBER_ACCOUNT_KEY);
      }
      onLoginSuccess(results, navigate);
    } catch (err) {
      setLoginError(t("login.errorInvalid"));
    } finally {
      setLoading(false);
    }
  };

  if (hasToken) {
    return <Navigate to="/menu" replace />;
  }

  return (
    <div
      style={{
        // 100dvh + 可捲動：虛擬鍵盤彈出縮小可視區時，表單仍可捲入視野，避免被鍵盤遮擋
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: `calc(24px + ${SAFE_AREA.top}) 16px calc(16px + ${SAFE_AREA.bottom})`,
        position: "relative",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        background:
          "linear-gradient(165deg, #007dda 0%, #005a9e 35%, #0a1628 100%)",
      }}
    >
      {/* 背景裝飾：柔和的圓形（fixed 不撐高捲動區，避免影響鍵盤彈出時的捲動） */}
      <div
        style={{
          position: "fixed",
          top: "-20%",
          right: "-30%",
          width: "80%",
          height: "80%",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.06)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "fixed",
          bottom: "-10%",
          left: "-20%",
          width: "60%",
          height: "50%",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.04)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "fixed",
          top: "30%",
          left: "-15%",
          width: "40%",
          height: "40%",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.03)",
          pointerEvents: "none",
        }}
      />

      <Card
        style={{
          width: "100%",
          maxWidth: 400,
          position: "relative",
          borderRadius: 16,
          boxShadow: "0 24px 48px rgba(0,0,0,0.2), 0 8px 16px rgba(0,0,0,0.1)",
          border: "1px solid rgba(255,255,255,0.12)",
          overflow: "hidden",
        }}
        styles={{ body: { padding: "32px 24px" } }}
      >
        {/* 卡片頂部色條 */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: "linear-gradient(90deg, #007dda, #00a0e9)",
          }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 14,
            marginBottom: 32,
            flexWrap: "wrap",
          }}
        >
          <img
            src="/logo.svg"
            alt="OmniEyes"
            width={44}
            height={44}
            style={{ display: "block", flexShrink: 0 }}
          />
          <span
            style={{
              fontSize: 28,
              fontWeight: 700,
              color: TEXT.heading,
              letterSpacing: "-0.03em",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              textShadow: "0 1px 2px rgba(0,0,0,0.06)",
            }}
          >
            MyFleet
          </span>
        </div>

        <Form
          name="login"
          layout="vertical"
          initialValues={{ remember: true, account: savedAccount }}
          onFinish={onFinish}
          autoComplete="off"
          size="large"
        >
          <Form.Item
            label={t("login.account")}
            name="account"
            rules={[{ required: true, message: t("login.accountRequired") }]}
          >
            <Input
              placeholder={t("login.accountPlaceholder")}
              onFocus={clearError}
              autoComplete="username"
            />
          </Form.Item>

          <Form.Item
            label={t("login.password")}
            name="password"
            rules={[{ required: true, message: t("login.passwordRequired") }]}
          >
            <Input.Password
              placeholder={t("login.passwordPlaceholder")}
              onFocus={clearError}
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item name="remember" valuePropName="checked">
            <Checkbox>{t("login.rememberAccount")}</Checkbox>
          </Form.Item>

          <Form.Item style={{ marginBottom: 8 }}>
            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={loading}
            >
              {t("login.submit")}
            </Button>
          </Form.Item>
          {loginError ? (
            <div
              role="alert"
              style={{
                textAlign: "center",
                color: "#f22020",
                fontSize: FONT.body,
                fontWeight: 600,
                marginTop: 8,
              }}
            >
              {loginError}
            </div>
          ) : null}
        </Form>

        {/* 客戶收到網址開啟時看到的第一個畫面就是這裡，安裝入口放這比藏在選單裡有效 */}
        <InstallAppButton />

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 8,
            marginTop: 22,
          }}
        >
          {[
            { lng: "zh", label: "中文" },
            { lng: "en", label: "English" },
            { lng: "ja", label: "日本語" },
          ].map(({ lng, label }) => {
            const active = i18n.language === lng;
            return (
              <span
                key={lng}
                role="button"
                onClick={() => changeLanguage(lng)}
                style={{
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: TOUCH_MIN,
                  padding: "8px 14px",
                  borderRadius: 10,
                  fontSize: FONT.secondary,
                  fontWeight: active ? 600 : 500,
                  color: active ? COLOR_PRIMARY : "#8c8c8c",
                  background: active ? "rgba(0, 125, 218, 0.1)" : "transparent",
                }}
              >
                {label}
              </span>
            );
          })}
        </div>
      </Card>

      {/* 最下方：版權 */}
      <div
        style={{
          marginTop: 24,
          textAlign: "center",
          fontSize: FONT.caption,
          color: "rgba(255,255,255,0.85)",
        }}
      >
        {t("login.copyright")}
      </div>
    </div>
  );
}
